/**
 * Depoimentos em vídeo:
 * - toca em loop e sem som o tempo todo (prévia ambiente, igual à PDP), sem precisar passar o mouse;
 * - clicar no play toca de verdade, com som, e troca o botão grande central
 *   pelos controles pequenos no canto (pausar/retomar e mudo/com som);
 * - ao parar o vídeo real, volta para a prévia ambiente (em loop e sem som);
 * - só um vídeo "de verdade" toca por vez.
 */
(() => {
  const liteMode = () => {
    const connection = navigator.connection || {};
    return Boolean(connection.saveData) || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const stopReal = (story) => {
    const video = story.querySelector('video');
    story.classList.remove('is-playing');
    const pauseBtn = story.querySelector('[data-story-pause]');
    const muteBtn = story.querySelector('[data-story-mute]');
    if (pauseBtn) pauseBtn.setAttribute('aria-pressed', 'true');
    if (muteBtn) muteBtn.setAttribute('aria-pressed', 'false');
    if (video) {
      video.muted = true;
      // Volta para a prévia ambiente em vez de ficar parado num quadro estático (só se estiver na tela).
      if (liteMode() || story.storyVisible === false) video.pause();
      else video.play().catch(() => {});
    }
  };

  const boot1 = (scope = document) =>
    scope.querySelectorAll('[data-story]').forEach((story) => {
    if (story.dataset.initialized) return;
    story.dataset.initialized = 'true';

    const video = story.querySelector('video');
    const playBtn = story.querySelector('[data-story-toggle]');
    const pauseBtn = story.querySelector('[data-story-pause]');
    const muteBtn = story.querySelector('[data-story-mute]');
    if (!video) return;

    const allStories = () => Array.from(document.querySelectorAll('[data-story]'));

    // Fonte do vídeo só quando o card chega perto da tela (não disputa banda com o banner/LCP).
    // Com "Economia de dados" ou "Reduzir movimento" no aparelho não há prévia ambiente: o vídeo
    // só é baixado quando a pessoa aperta o play.
    const lite = liteMode();
    // A prévia usa a versão leve (480p) e só busca o começo do vídeo (preload "metadata"); o resto vem
    // aos poucos enquanto toca. Antes era a de 1080p com preload "auto": cada card baixava o arquivo
    // inteiro (até ~44 MB) assim que chegava perto da tela. Se a versão leve não existir, usa a original.
    const setSource = (src) => {
      let source = video.querySelector('source');
      if (!source) {
        source = document.createElement('source');
        if (video.dataset.storyType) source.type = video.dataset.storyType;
        source.addEventListener('error', () => {
          const fallback = video.dataset.storySrcFallback;
          if (fallback && source.src !== new URL(fallback, location.href).href) setSource(fallback);
        });
        video.appendChild(source);
      }
      source.src = src;
      video.load();
    };
    const attach = () => {
      if (!video.dataset.storySrc || video.dataset.storyLoaded || video.currentSrc) return;
      video.dataset.storyLoaded = 'true';
      video.preload = 'metadata';
      setSource(video.dataset.storySrc);
    };
    // Play de verdade (com som): troca para a versão melhor (720p) uma vez.
    const upgrade = () => {
      const hd = video.dataset.storySrcHd;
      if (!hd || video.dataset.storyHd || hd === video.dataset.storySrc) return;
      video.dataset.storyHd = 'true';
      setSource(hd);
    };
    story.storyAttach = attach;
    if (video.dataset.storySrc && !video.currentSrc && !lite) {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              io.disconnect();
              attach();
            }
          },
          { rootMargin: '300px' }
        );
        io.observe(story);
      } else {
        attach();
      }
    }

    // Prévia ambiente só enquanto o card está visível: fora da tela (rolagem ou outro slide do
    // carrossel) o vídeo pausa e para de gastar CPU/bateria.
    story.storyVisible = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            story.storyVisible = entry.isIntersecting;
            if (story.classList.contains('is-playing') || lite) return;
            if (entry.isIntersecting) video.play().catch(() => {});
            else video.pause();
          }),
        { threshold: 0.25 }
      ).observe(story);
    }

    // Prévia ambiente: toca em loop e sem som assim que o vídeo estiver pronto.
    video.muted = true;
    const tryAmbientPlay = () => {
      if (story.classList.contains('is-playing') || lite || !story.storyVisible) return;
      video.play().catch(() => {});
    };
    if (video.readyState >= 2) {
      tryAmbientPlay();
    } else {
      video.addEventListener('loadeddata', tryAmbientPlay, { once: true });
    }

    // Play "de verdade": som ligado e controles no canto.
    if (playBtn) {
      playBtn.addEventListener('click', () => {
        allStories().forEach((other) => other !== story && stopReal(other));

        story.classList.add('is-playing');
        attach();
        upgrade();
        video.currentTime = 0;
        video.muted = false;
        if (pauseBtn) pauseBtn.setAttribute('aria-pressed', 'true');
        if (muteBtn) muteBtn.setAttribute('aria-pressed', 'false');

        video.play().catch(() => {
          // Se o navegador recusar o som, toca mudo em vez de falhar silenciosamente.
          video.muted = true;
          if (muteBtn) muteBtn.setAttribute('aria-pressed', 'true');
          video.play().catch(() => stopReal(story));
        });
      });
    }

    if (pauseBtn) {
      pauseBtn.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          pauseBtn.setAttribute('aria-pressed', 'true');
        } else {
          video.pause();
          pauseBtn.setAttribute('aria-pressed', 'false');
        }
      });
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        muteBtn.setAttribute('aria-pressed', String(video.muted));
      });
    }

    video.addEventListener('ended', () => {
      if (story.classList.contains('is-playing')) stopReal(story);
    });
  });
  boot1();
  document.addEventListener('shopify:section:load', (event) => boot1(event.target));
})();
